import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useSearchResults } from "@apis/searchResult";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import { mergeDiamondsAndPromotionsPerPage } from "@scenes/searchResults/components/utils";
import { useTheme } from "styled-components/native";
import { useCallback, useEffect, useRef } from "react";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";

export const useSearchResultsData = (
  productType: ProductCategory,
  selectedViewType: SearchResultsAndFavoritesEnum,
  payload: any,
  saved_searches: any,
  disableInitialPrefetch = false,
  enabled = true
) => {
  const userInfo = useUserInfo();
  const priceMode = userInfo?.user?.price_mode;
  const theme = useTheme();

  let requestPayload = {
    page_size:
      theme.isLargeScreen &&
      selectedViewType === SearchResultsAndFavoritesEnum.LIST
        ? SearchResultsAndFavoritesEnum.WEB_LIST_PAGE_SIZE
        : SearchResultsAndFavoritesEnum.PAGE_SIZE,
    price_mode: priceMode,
    ...payload,
    lab_grown:
      productType === ProductCategory.LAB_GROWN_DIAMOND ||
      payload?.lab_grown === true ||
      payload?.sub_type === ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE,
    results_view_type:
      selectedViewType === SearchResultsAndFavoritesEnum.LIST_EXPANDED
        ? SearchResultsAndFavoritesEnum.LIST
        : selectedViewType,
  };

  if (!("with_available_items" in requestPayload)) {
    requestPayload = { ...requestPayload, with_available_items: false };
  }

  if (saved_searches) {
    let parsed = saved_searches;
    if (typeof saved_searches === "string") {
      try {
        parsed = JSON.parse(decodeURI(saved_searches).replace(/"|"/g, '"'));
      } catch {
        parsed = null;
      }
    }
    if (
      parsed &&
      typeof parsed === "object" &&
      "with_available_items" in parsed
    ) {
      requestPayload = {
        ...requestPayload,
        with_available_items: parsed.with_available_items,
      };
    }
  }

  const {
    data,
    fetchNextPage: originalFetchNextPage,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
  } = useSearchResults(
    productType,
    requestPayload,
    selectedViewType,
    userInfo?.user?.id,
    saved_searches,
    userInfo.user?.currency,
    userInfo.user?.locale,
    enabled
  );

  const isFetchingRef = useRef(false);

  const fetchNextPage = useCallback(async () => {
    if (isFetchingRef.current || isFetchingNextPage || !hasNextPage) {
      return;
    }

    isFetchingRef.current = true;

    try {
      return await originalFetchNextPage();
    } finally {
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 100);
    }
  }, [originalFetchNextPage, isFetchingNextPage, hasNextPage]);

  useEffect(() => {
    if (
      enabled &&
      data?.pages?.length === 1 &&
      !disableInitialPrefetch &&
      !isLoading &&
      !isFetchingNextPage &&
      hasNextPage
    ) {
      originalFetchNextPage().then();
    }
  }, [
    enabled,
    data?.pages?.length,
    disableInitialPrefetch,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  ]);

  const products = mergeDiamondsAndPromotionsPerPage(data?.pages || []);
  const promotionsCount =
    data?.pages
      .map((page) => page?.data?.response?.body?.promotions)
      .flat()
      .filter(Boolean)?.length || 0;

  const totalItems =
    data?.pages?.[0]?.data?.response?.header?.total_diamonds_found ||
    data?.pages?.[0]?.data?.response?.header?.total_stones_found ||
    0;

  const serverResponseMessage =
    data?.pages?.[0]?.data?.response?.header?.message;

  return {
    products,
    promotionsCount,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    totalItems,
    hasNextPage,
    serverResponseMessage,
  };
};
